import { ArrowBack } from '@styled-icons/material-outlined';
import {
  ContentMutator,
  MutateContent,
  ISpaceCustomization,
  ITemplate,
  TEMPLATE_CATEGORY,
  TEMPLATE_TYPE,
} from '@typedream/data';
import { BaseDreamEditor } from '@typedream/dream-editor/src/types';
import {
  getLastNodeByLevel,
  getNode,
  getPath,
  insertNodes,
  removeNodes,
  toDOMNode,
  withoutSavingHistory,
} from '@udecode/plate-core';
import { useTheme } from 'next-themes';
import Image from 'next/image';
import { useEffect, useState } from 'react';

import ClientOnlyPortal from '@/components/ClientOnlyPortal';
import LoadingSmall from '@/components/LoadingSmall';
import { DARK_THEME } from '@/constants';
import { useSpaceIDFromPath } from '@/data/space/hooks';
import flags from '@/store/flags';
import Floating from '@/sharedComponents/TextEditor/components/Floating';
import { DreamEditor, DreamElementOrText, DreamValue } from '@/sharedComponents/TextEditor/plate';
import {
  useFeatureFlag,
  useGetSpaceQuery,
  useGetTemplateQuery,
  useProcessTemplateBeforeUsageMutation,
} from '@/store';

import { DreamBlock, FormElement } from '@typedream/core';
import { cloneDeep, omit } from 'lodash';
import { useMachine } from '@xstate/react';
import { assign } from 'xstate';
import { Text } from '@typedream/ui';
import classNames from 'classnames';
import { sectionTemplatesContentMachine } from './SectionTemplatesContent.fsm';
import { Template } from './Template';
import styles from './SectionTemplatesContent.module.css';

interface SectionTemplatesContentProps {
  templates: any[];
  loading: boolean;
  err: any;
  editor: DreamEditor | BaseDreamEditor;
  sectionCategory: TEMPLATE_CATEGORY;
  resetTab: () => void;
}

export function SectionTemplatesContent({
  templates,
  loading,
  err,
  editor,
  sectionCategory,
  resetTab,
}: SectionTemplatesContentProps) {
  const [hoveredDOMNode, sethoveredDOMNode] = useState<HTMLElement | null>(null);
  const spaceID = useSpaceIDFromPath();
  const { theme } = useTheme();
  const { mutate: processTemplateBeforeUsage } = useProcessTemplateBeforeUsageMutation();
  const isDreamEditorEnabled = useFeatureFlag(flags.DreamEditor);

  const revalidateSpaceData = async () => {
    const resp = await useGetSpaceQuery.run([spaceID]).unwrap();

    if (!isDreamEditorEnabled) {
      (editor as DreamEditor)?.store
        ?.getState()
        ?.setFormReferences(resp?.customization?.form_reference);
    }
  };

  const getMutator: (s: ISpaceCustomization) => ContentMutator = (sp) => (c) => {
    if (!('type' in c)) return c;
    if (c.type !== 'form') return c;

    const formContent = sp.form_reference?.content?.[c.form_id].value[0] as FormElement;

    if (!formContent) return c;

    return {
      ...formContent,
      ...omit(c, ['children']),
      form_id: c.form_id,
    };
  };

  const toDreamValue: (c?: DreamValue | DreamBlock) => DreamValue = (c) => {
    if (!c) return [];

    if (!Array.isArray(c)) {
      return [c];
    }
    return c;
  };

  const [machineState, machineSend] = useMachine(sectionTemplatesContentMachine, {
    actions: {
      assignHoveredTemplate: assign((context, event) => {
        if (event.type === 'ON_HOVER_TEMPLATE') {
          const mutator = getMutator(event.template.customization || {});
          const tc = MutateContent(toDreamValue(event.template.published_content), [mutator]) || [];
          const newEditorHoveredTemplatePreviewNode = tc[0] as DreamElementOrText;

          return {
            hoveredTemplate: event.template,
            hoveredTemplatePreviewNode: event?.template?.published_content,
            editorHoveredTemplatePreviewNode: newEditorHoveredTemplatePreviewNode || {
              type: 'p',
              children: [{ text: '' }],
            },
          };
        }
        if (
          event.type ===
          "done.invoke.Section Template Content Machine.Preview Template.Fetch template's published content:invocation[0]"
        ) {
          const data = event.data as ITemplate<TEMPLATE_TYPE.SECTION> | void;
          const newHoveredTemplate = cloneDeep(context.hoveredTemplate);

          let originalNode: DreamBlock | undefined;
          let newEditorHoveredTemplatePreviewNode = context?.editorHoveredTemplatePreviewNode;

          if (data && data.published_content) {
            const mutator = getMutator(data.customization || {});
            const tc = MutateContent(toDreamValue(data.published_content), [mutator]) || [];
            newEditorHoveredTemplatePreviewNode = tc[0] as DreamElementOrText;
            originalNode = data.published_content;
          }

          return {
            hoveredTemplate: newHoveredTemplate,
            hoveredTemplatePreviewNode: originalNode ?? context.hoveredTemplatePreviewNode,
            editorHoveredTemplatePreviewNode: newEditorHoveredTemplatePreviewNode,
          };
        }
        return {};
      }),
      assignErrorGetPublicTemplate: (_) => {
        // Handle error
      },

      insertPreviewTemplateToEditor: assign((context) => {
        let path = getPath(editor, editor.selection);
        const [, lastPath] = getLastNodeByLevel(editor, 1);

        path = path || (sectionCategory === TEMPLATE_CATEGORY.FOOTER ? lastPath : [0, 0]);
        path = path.slice(0, 2);
        path[path.length - 1] += 1;

        withoutSavingHistory(editor, () => {
          insertNodes(editor, context?.editorHoveredTemplatePreviewNode, { at: path });
        });

        return {
          position: path || null,
        };
      }),
      insertTemplateToEditor: (context, event) => {
        const newContent = event.data as DreamValue;

        if (!context?.position) return;
        // // remove the hovered template
        withoutSavingHistory(editor, () => {
          removeNodes(editor, { at: context?.position });
        });

        insertNodes(editor, newContent, { at: context?.position });

        revalidateSpaceData();
        resetTab();
      },
      resetPreviewedTemplate: assign((context, event) => {
        if (event.type === 'ON_MOUSE_LEAVE') {
          if (context?.position) {
            withoutSavingHistory(editor, () => {
              removeNodes(editor, { at: context.position });
            });
          }
        }
        return {
          hoveredTemplate: undefined,
          position: undefined,
          hoveredTemplatePreviewNode: undefined,
          hoveredTemplateDOMNode: null,
          editorHoveredTemplatePreviewNode: undefined,
        };
      }),
    },
    services: {
      mutateProcessTemplateBeforeUsage: async (context) => {
        // Check whether the `hoveredTemplatePreviewNode` (which is the original
        // node's published content from the template list) exist. If it exist, we
        // can assume it is the hardcoded template and it doesn't need to be processed.
        if (context?.hoveredTemplatePreviewNode) return context?.hoveredTemplatePreviewNode;
        return processTemplateBeforeUsage(spaceID as string, context?.hoveredTemplate.id, [
          context?.hoveredTemplatePreviewNode,
        ] as DreamValue).unwrap();
      },

      getPublicTemplateQuery: async (context) =>
        // TODO: Might be able to use internal cache
        useGetTemplateQuery.run([spaceID, context?.hoveredTemplate?.id]).then((res) => res.data),
    },
  });

  // Note: DOMNode is used to add "Click to insert" badge
  useEffect(() => {
    let interval = null;
    const hoveredNode = machineState?.context?.editorHoveredTemplatePreviewNode;
    const position = machineState?.context?.position;
    if (hoveredNode && editor && position) {
      interval = setInterval(() => {
        const domNode = toDOMNode(editor, getNode(editor, position));
        sethoveredDOMNode(domNode);

        if (domNode) {
          clearInterval(interval);
        }
      }, 100);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [
    editor,
    machineState?.context?.editorHoveredTemplatePreviewNode,
    machineState?.context?.position,
  ]);

  return (
    <div className="flex flex-col gap-4 h-full overflow-auto flex-1 pb-8">
      {loading && (
        <div className="flex items-center justify-center gap-2">
          <LoadingSmall loading={loading} />
          <span className="is-subtitle">Grabbing templates...</span>
        </div>
      )}

      {!loading && templates.length === 0 && (
        <div
          className="flex flex-col items-center justify-start gap-4 pt-8 px-4 text-center"
          style={{
            height: 'calc(100% - 52px)',
          }}
        >
          <Image
            src="/images/no_templates_found.svg"
            alt="no templates found"
            width={160}
            height={160}
          />
          <Text className="font-semibold leading-[24px]">No templates found</Text>
        </div>
      )}

      {err && (
        <span className="with-danger-text-color is-subtitle-size has-fullwidth has-text-centered p-3">
          {err}
        </span>
      )}

      {templates.map((template) => (
        <div key={template.id} style={{ marginLeft: 16, marginRight: 16 }}>
          <Template
            img={
              (theme === DARK_THEME && template.metadata.image_sidebar_dark) ||
              (theme === DARK_THEME && template.metadata.image_dark) ||
              template.metadata.image_sidebar ||
              template.metadata.image
            }
            active={
              (machineState?.context?.hoveredTemplate?.id &&
                machineState?.context?.hoveredTemplate?.id === template.id) ||
              machineState?.context?.hoveredTemplate === template
            }
            onHover={() => {
              machineSend({ type: 'ON_HOVER_TEMPLATE', template });
            }}
            onMouseLeave={() => {
              machineSend({ type: 'ON_MOUSE_LEAVE' });
            }}
            onSelectTemplate={() => {
              machineSend({ type: 'ON_CLICK_TEMPLATE' });
            }}
          />
        </div>
      ))}

      {hoveredDOMNode && machineState?.context?.position && (
        <ClientOnlyPortal>
          <Floating
            reference={hoveredDOMNode}
            autoUpdate
            options={{
              placement: 'top',
            }}
            style={{
              zIndex: 9999,
            }}
          >
            <div
              className={classNames(
                styles.bounceInLeftAnimation,
                'absolute flex gap-2.5 text-[var(--accent)] font-semibold bg-[var(--primary-25)]',
                'border border-2 border-solid border-[var(--accent)] rounded-full',
                'py-2.5 px-[18px] whitespace-nowrap'
              )}
              style={{
                top: 24,
                left: '50%',
                transform: 'translate(-50%)',
                boxShadow: '0px 10px 20px rgba(94, 93, 239, 0.12)',
                zIndex: 999,
              }}
            >
              <ArrowBack size={24} />
              Click to insert section
            </div>
          </Floating>
        </ClientOnlyPortal>
      )}
    </div>
  );
}
